import type { ControllerFieldState, FieldValues, UseFormReturn } from 'react-hook-form';
import type { FormFieldConfig } from '../types';
import type { BaseComponentProps } from './types';

/**
 * Field accessibility attributes generated by useFieldIds
 */
export interface FieldIds {
  /** The unique ID for the field input element */
  fieldId: string;
  /** The ID for the label element */
  labelId: string;
  /** The ID for the description element (undefined if no description) */
  descriptionId: string | undefined;
  /** The ID for the error message element (undefined if no error) */
  errorId: string | undefined;
  /** The aria-describedby attribute value (combined description and error IDs) */
  describedBy: string | undefined;
  /** Whether the field has an error */
  isInvalid: boolean;
}

/**
 * Generates consistent IDs and aria attributes for form fields.
 * Extracts the common ID construction pattern used across all declarative form fields.
 *
 * @param fieldConfig - The field configuration containing name and description
 * @param fieldState - The field state from react-hook-form containing error info
 * @param customId - Optional custom ID to use instead of field name
 * @returns Object with all generated IDs and aria attributes
 *
 * @example
 * ```tsx
 * const { fieldId, labelId, descriptionId, errorId, describedBy, isInvalid } = getFieldIds(
 *   fieldConfig,
 *   fieldState,
 *   componentProps.id
 * );
 *
 * <input
 *   id={fieldId}
 *   aria-describedby={describedBy}
 *   aria-invalid={isInvalid}
 * />
 * <FormLabel id={labelId} htmlFor={fieldId}>...</FormLabel>
 * {descriptionId && <FormDescription id={descriptionId}>...</FormDescription>}
 * {errorId && <FormMessage id={errorId}>...</FormMessage>}
 * ```
 */
export function getFieldIds<TFieldValues extends FieldValues>(
  fieldConfig: FormFieldConfig<TFieldValues>,
  fieldState: ControllerFieldState,
  customId?: string
): FieldIds {
  const fieldId = customId || String(fieldConfig.name);
  const labelId = `${fieldId}-label`;
  const descriptionId = fieldConfig.description ? `${fieldId}-description` : undefined;
  const isInvalid = !!fieldState.error;
  const errorId = isInvalid ? `${fieldId}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return {
    fieldId,
    labelId,
    descriptionId,
    errorId,
    describedBy,
    isInvalid,
  };
}

/**
 * Permissive form state type that allows callers to pass functions typed with
 * their specific form values without contravariance issues.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFormState = UseFormReturn<any>;

/**
 * Utility type for field components that need options that can be either static or dynamic.
 * The function form receives the form context for dynamic option generation.
 */
export type FieldComponentWithOptions<
  TComponent extends React.ElementType,
  TOption,
> = BaseComponentProps<TComponent> & {
  options: TOption[] | ((formState: AnyFormState) => TOption[]);
};

/**
 * Utility type for field components that need a placeholder
 */
export type FieldComponentWithPlaceholder<TComponent extends React.ElementType> = BaseComponentProps<TComponent> & {
  placeholder?: string;
};
